import React, { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { enablePush, disablePush, snoozePush, pushStatus, getPushSubscription, pushSupported } from "@/lib/push";

/** Bell menu: turn ticket phone notifications on/off or snooze them
 * (snoozed = emails keep coming, phone stays quiet). */
export default function PushToggle({ client, base = "/push", targetUrl = "/", testidPrefix = "push", alwaysVisible = false, label = "", className = "" }) {
  const [open, setOpen] = useState(false);
  const [on, setOn] = useState(false);
  const [snoozedUntil, setSnoozedUntil] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setOn(!!(await getPushSubscription()) && Notification.permission === "granted");
    try { const s = await pushStatus(client, base); setSnoozedUntil(s.snoozed_until); } catch { /* signed out */ }
  }, [client, base]);
  useEffect(() => { if (pushSupported()) refresh(); }, [refresh]);

  const supported = pushSupported();
  if (!supported && !alwaysVisible) return null;
  // Phones/tablets (or the installed app) only — desktop browsers don't get the bell.
  const mobileLike =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true ||
    /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent || "");
  if (!mobileLike && !alwaysVisible) return null;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await refresh(); setOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const snoozed = snoozedUntil && new Date(snoozedUntil) > new Date();
  const Icon = !on ? BellOff : snoozed ? Bell : BellRing;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!supported}
        className={`${label ? "inline-flex" : "grid"} h-9 ${label ? "w-auto" : "w-9"} place-items-center rounded-sm border transition-colors disabled:opacity-60 ${on && !snoozed ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-border text-muted-foreground hover:text-foreground"} ${className}`}
        title={!supported ? "Phone notifications are not supported by this browser" : !on ? "Phone notifications are off" : snoozed ? "Notifications snoozed — emails only" : "Phone notifications are on"}
        data-testid={`${testidPrefix}-toggle`}
      >
        <Icon className="w-4 h-4" />
        {label && <span>{label}</span>}
        {snoozed && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-border rounded-md shadow-xl py-1 w-64 text-sm" data-testid={`${testidPrefix}-menu`}>
            <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Ticket notifications</p>
            {!on ? (
              <button type="button" disabled={busy} onClick={() => act(() => enablePush(client, base, targetUrl), "Phone notifications are ON for this device.")}
                className="w-full text-left px-3 py-2 hover:bg-slate-100 font-semibold text-emerald-700" data-testid={`${testidPrefix}-enable`}>
                Turn ON phone notifications
              </button>
            ) : (
              <>
                {snoozed ? (
                  <button type="button" disabled={busy} onClick={() => act(() => snoozePush(client, base, 0), "Notifications resumed.")}
                    className="w-full text-left px-3 py-2 hover:bg-slate-100 font-semibold text-emerald-700" data-testid={`${testidPrefix}-resume`}>
                    Resume now (snoozed until {new Date(snoozedUntil).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })})
                  </button>
                ) : (
                  <>
                    <button type="button" disabled={busy} onClick={() => act(() => snoozePush(client, base, 1), "Snoozed for 1 hour — you'll still get emails.")}
                      className="w-full text-left px-3 py-2 hover:bg-slate-100" data-testid={`${testidPrefix}-snooze-1`}>Snooze 1 hour</button>
                    <button type="button" disabled={busy} onClick={() => act(() => snoozePush(client, base, 8), "Snoozed for 8 hours — you'll still get emails.")}
                      className="w-full text-left px-3 py-2 hover:bg-slate-100" data-testid={`${testidPrefix}-snooze-8`}>Snooze 8 hours</button>
                    <button type="button" disabled={busy} onClick={() => act(() => snoozePush(client, base, 24), "Snoozed for 24 hours — you'll still get emails.")}
                      className="w-full text-left px-3 py-2 hover:bg-slate-100" data-testid={`${testidPrefix}-snooze-24`}>Snooze 24 hours</button>
                  </>
                )}
                <button type="button" disabled={busy} onClick={() => act(() => disablePush(client, base), "Phone notifications turned OFF for this device.")}
                  className="w-full text-left px-3 py-2 hover:bg-slate-100 text-red-600 border-t border-border" data-testid={`${testidPrefix}-disable`}>
                  Turn OFF on this device
                </button>
              </>
            )}
            <p className="px-3 py-1.5 text-[11px] text-muted-foreground border-t border-border">Snooze = emails keep coming, your phone stays quiet.</p>
          </div>
        </>
      )}
    </div>
  );
}
